/**
 * Экран входа: одно окно и две стадии — ввод канала, затем ввод кода.
 *
 * Экран один намеренно. Второй смонтированный узел означал бы два места, где
 * живёт введённое, и расхождение между ними видно только на живом сервере.
 *
 * ПЕРЕВОРОТ блока `WEB-1.1` (спецификация §7). Было: «одно окно и два
 * состояния — ввод телефона, затем ввод кода», стадия называлась `'phone'`.
 * Стало: первая стадия — **ввод канала**, потому что каналов два, и значение
 * `'phone'` при выбранной почте читалось бы ложью. Канал — величина, отдельная
 * от стадии.
 *
 * Второе перевёрнутое утверждение — «состояние отказа тоже одно». Общее
 * состояние отказа осталось: причина по-прежнему не разбирается ни по тексту
 * сообщения, ни по транспортному статусу. Рядом с ним появился показ сообщений
 * сервера **по полям**, и это не ветвление: сервер называет поле и пишет текст,
 * клиент кладёт текст туда, куда указали, и ни одного имени поля не знает.
 *
 * Проверок формата телефона и почты здесь нет ни одной (решение `D123`).
 * Контракт говорит дословно: канал выбирает клиент, а годность значения
 * проверяет сервер. Распознавание «это похоже на почту» было бы правилом в
 * клиенте, и потому каналов два **явных**, а не одно поле с угадыванием.
 *
 * Согласие с условиями (решение `D124`): отмечено — уходит истинным; не
 * отмечено — ключа в теле **нет вовсе**. Кто новый пользователь, а кто прежний,
 * решает сервер, и отправленное «нет» было бы утверждением клиента о том, чего
 * он не знает. Юридического текста условий экран не сочиняет: его пишет
 * человек, а временная строка была бы заглушкой.
 *
 * Срок кода и срок повторной отправки на экран не попадают: счётчика и
 * повторной отправки в блоке нет, а число без поведения вокруг него закрывает
 * требование на вид.
 *
 * Строки приходят набором локализаций — литералом на экране нет ни одной.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient, AuthChannel, AuthSession } from '../api/client';
import type { Strings } from '../i18n';
import { createFieldMessageSlot, renderFieldMessages } from './state';

/** Две стадии одного экрана: ввод канала, затем ввод кода. */
export type LoginStage = 'entry' | 'code';

export interface LoginScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly onAuthenticated: (session: AuthSession) => void;
}

export interface LoginScreen {
  readonly element: HTMLElement;
  readonly stage: LoginStage;
  /** Выбранный канал одноразового кода. Телефон — по умолчанию. */
  readonly channel: AuthChannel;
  readonly failed: boolean;
  selectChannel(channel: AuthChannel): void;
  /** Отправка первой стадии. Служит обоим каналам. */
  submitEntry(value: string): Promise<void>;
  /**
   * Прежнее имя отправки первой стадии.
   *
   * Оно живёт, а не оставлено про запас: запечатанный набор `w0-3a` зовёт
   * телефонную ветку этим именем, и его файл не правится ни байтом. Это одна и
   * та же функция, а не вторая реализация.
   */
  submitPhone(value: string): Promise<void>;
  setTermsAccepted(accepted: boolean): void;
  submitCode(code: string): Promise<void>;
}

/** Канал по умолчанию: у водителей Средней Азии почты часто нет. */
const DEFAULT_CHANNEL: AuthChannel = 'phone';

export function createLoginScreen(options: LoginScreenOptions): LoginScreen {
  const { client, strings, onAuthenticated } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const form = document.createElement('form');
  const channels = document.createElement('div');
  const phoneChoice = document.createElement('button');
  const emailChoice = document.createElement('button');
  const caption = document.createElement('label');
  const captionText = document.createElement('span');
  const field = document.createElement('input');
  const fieldMessage = createFieldMessageSlot();
  const terms = document.createElement('label');
  const termsText = document.createElement('span');
  const termsBox = document.createElement('input');
  const termsMessage = createFieldMessageSlot();
  const submit = document.createElement('button');
  const notice = document.createElement('p');

  element.className = 'screen screen-login';
  form.className = 'form';
  channels.className = 'channels';
  caption.className = 'form-field';
  captionText.className = 'form-field__caption';
  terms.className = 'form-field form-field--terms';
  submit.className = 'action action--primary';
  notice.className = 'notice';

  heading.textContent = strings.login_title;
  notice.textContent = strings.login_failed;
  phoneChoice.type = 'button';
  phoneChoice.textContent = strings.login_channel_phone;
  emailChoice.type = 'button';
  emailChoice.textContent = strings.login_channel_email;
  termsText.textContent = strings.login_terms_label;
  termsBox.type = 'checkbox';
  termsBox.name = 'terms_accepted';
  submit.type = 'submit';

  channels.append(phoneChoice, emailChoice);
  caption.append(captionText, field, fieldMessage);
  terms.append(termsBox, termsText, termsMessage);
  element.append(heading, form);

  let stage: LoginStage = 'entry';
  let channel: AuthChannel = DEFAULT_CHANNEL;
  let failed = false;
  let termsAccepted = false;
  /** Введённое значение и канал, которым его отправили. */
  let entered = '';
  let sentChannel: AuthChannel = DEFAULT_CHANNEL;

  function render(): void {
    const asking = stage === 'entry';
    const byPhone = channel === 'phone';

    // Имя поля — то же, что имя поля запроса: по нему сервер называет место,
    // куда положить своё сообщение. Оно стоит в разметке, а не в разборе.
    field.name = asking ? (byPhone ? 'phone' : 'email') : 'code';
    field.id = field.name;
    caption.htmlFor = field.name;
    field.type = asking && byPhone ? 'tel' : 'text';

    captionText.textContent = asking
      ? (byPhone ? strings.login_phone_label : strings.login_email_label)
      : strings.login_code_label;
    submit.textContent = asking ? strings.login_code_request : strings.login_code_submit;

    phoneChoice.setAttribute('aria-pressed', String(byPhone));
    emailChoice.setAttribute('aria-pressed', String(!byPhone));

    if (asking) {
      form.replaceChildren(channels, caption, submit);
    } else {
      form.replaceChildren(caption, terms, submit);
    }

    if (failed) {
      element.append(notice);
    } else {
      notice.remove();
    }
  }

  /**
   * Смена канала до отправки очищает введённое.
   *
   * Иначе номер, набранный при выбранном телефоне, уедет полем почты — и
   * человек получит отказ, причину которого не увидит.
   */
  function selectChannel(chosen: AuthChannel): void {
    if (stage !== 'entry' || chosen === channel) {
      return;
    }

    channel = chosen;
    field.value = '';
    renderFieldMessages(form, []);
    render();
  }

  function failureOf(error: unknown): void {
    failed = true;
    renderFieldMessages(form, error instanceof ApiFailure ? error.details : []);
  }

  async function submitEntry(value: string): Promise<void> {
    renderFieldMessages(form, []);

    try {
      // Канал запоминается вместе со значением: подтверждение уходит тем же
      // каналом, которым выслан код, и подменить его на второй стадии нечем.
      await client.requestAuthCode(channel === 'phone' ? { phone: value } : { email: value });

      entered = value;
      sentChannel = channel;
      failed = false;
      stage = 'code';
      field.value = '';
    } catch (error) {
      failureOf(error);
    }

    render();
  }

  function setTermsAccepted(accepted: boolean): void {
    termsAccepted = accepted;
    termsBox.checked = accepted;
  }

  async function submitCode(code: string): Promise<void> {
    renderFieldMessages(form, []);

    try {
      const session = await client.verifyAuthCode(verifyInput(code));

      failed = false;
      onAuthenticated(session);
    } catch (error) {
      failureOf(error);
    }

    render();
  }

  /**
   * Тело подтверждения: единственный ключ канала, код и — только если флажок
   * отмечен — согласие. Отсутствие ключа и значение «нет» это разные ответы, и
   * второй клиенту давать нечем.
   */
  function verifyInput(code: string) {
    if (sentChannel === 'phone') {
      return termsAccepted
        ? ({ phone: entered, code, terms_accepted: true } as const)
        : ({ phone: entered, code } as const);
    }

    return termsAccepted
      ? ({ email: entered, code, terms_accepted: true } as const)
      : ({ email: entered, code } as const);
  }

  phoneChoice.addEventListener('click', () => {
    selectChannel('phone');
  });

  emailChoice.addEventListener('click', () => {
    selectChannel('email');
  });

  termsBox.addEventListener('change', () => {
    setTermsAccepted(termsBox.checked);
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const value = field.value;

    void (stage === 'entry' ? submitEntry(value) : submitCode(value));
  });

  render();

  return {
    element,

    get stage(): LoginStage {
      return stage;
    },

    get channel(): AuthChannel {
      return channel;
    },

    get failed(): boolean {
      return failed;
    },

    selectChannel,
    submitEntry,
    submitPhone: submitEntry,
    setTermsAccepted,
    submitCode,
  };
}
